import { Injectable } from '@angular/core';
import { ConfigService } from '@app/Services/configs';
import { AlertService, AlertType } from '@app/Services/Utilities/alert.service';
import { DefaultGridConfigService } from '@app/Services/default-grid-config.service';
import { GridApi, ColDef, ColumnState, ApplyColumnStateParams } from 'ag-grid-community';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { ColumnConfig } from '@app/Store/reducers/column-config.reducer';

export interface GridSettings {
    id: string;
    timestamp: string;
    columnState: any[];
    gridOptions: {
        paginationPageSize?: number;
        filterModel?: any;
    };
}

@Injectable({
    providedIn: 'root',
})
export class TDGridSettingsService {
    private loadingStates = new Map<string, BehaviorSubject<boolean>>();
    private errorStates = new Map<string, BehaviorSubject<string | null>>();

    private pinnedStateMap = new Map<string, ColumnState[]>(); // Store pinned state per grid
    private resizeListeners = new Map<string, () => void>(); // Store resize listeners
    private columnEventListeners = new Map<string, () => void>(); // Store column event listeners

    constructor(
        private alertService: AlertService,
        private configService: ConfigService,
        private defaultGridConfigService: DefaultGridConfigService,
    ) {}

    saveGridSettings(gridApi: GridApi, gridId: string): Observable<{ success: boolean; message: string }> {
        if (!gridApi || !gridId) {
            console.warn('Cannot save: Grid API or Grid ID not provided');
            return new Observable((observer) => {
                observer.error({ success: false, message: 'Grid API or Grid ID not provided' });
            });
        }

        // Set loading state
        this.setLoadingState(gridId, true);
        this.setErrorState(gridId, null);

        const gridState: GridSettings = {
            id: gridId,
            timestamp: new Date().toISOString(),
            columnState: gridApi.getColumnState(), // This includes pinned state
            gridOptions: {
                paginationPageSize: gridApi.paginationGetPageSize(),
                filterModel: gridApi.getFilterModel(),
            },
        };

        return new Observable((observer) => {
            this.configService.saveConfig({ name: gridState.id, value: JSON.stringify(gridState) }).subscribe({
                next: (result) => {
                    this.setLoadingState(gridId, false);
                    this.alertService.alert(AlertType.Success, 'Grid settings saved successfully', 'Success');
                    observer.next(result);
                    observer.complete();
                },
                error: (error) => {
                    this.setLoadingState(gridId, false);
                    this.setErrorState(gridId, error.message || 'Error saving grid settings');
                    console.error('❌ Error saving grid settings via API:', error);
                    this.alertService.alert(AlertType.Error, 'Error saving grid settings', 'Error');
                    observer.error(error);
                },
            });
        });
    }

    loadGridSettings(gridId: string): Observable<GridSettings | null> {
        if (!gridId) {
            console.warn('Cannot load: Grid ID not provided');
            return new Observable((observer) => {
                observer.next(null);
                observer.complete();
            });
        }

        // Set loading state
        this.setLoadingState(gridId, true);
        this.setErrorState(gridId, null);

        return new Observable((observer) => {
            this.configService.getConfigByName(gridId).subscribe({
                next: (savedState) => {
                    this.setLoadingState(gridId, false);
                    if (savedState) {
                        console.log('📥 Grid settings loaded successfully via API', savedState);
                        observer.next(JSON.parse(savedState.value));
                    } else {
                        console.log(`ℹ️ No saved settings found for grid: ${gridId}`);
                        observer.next(null);
                    }
                    observer.complete();
                },
                error: (error) => {
                    this.setLoadingState(gridId, false);
                    this.setErrorState(gridId, 'Error loading grid settings');
                    console.error('❌ Error loading grid settings via API:', error);
                    observer.error(error);
                },
            });
        });
    }

    applyGridSettings(gridApi: GridApi, gridId: string): Observable<boolean> {
        if (!gridApi || !gridId) {
            console.warn('Cannot apply: Grid API or Grid ID not provided');
            return new Observable((observer) => {
                observer.next(false);
                observer.complete();
            });
        }

        return new Observable((observer) => {
            // Extract entity name from grid ID (for master grids: new-master-grid-{entityName})
            const isMasterGrid = gridId.startsWith('new-master-grid-');
            const entityName = isMasterGrid ? gridId.replace('new-master-grid-', '') : gridId;

            this.loadGridSettings(gridId).subscribe({
                next: (savedState) => {
                    if (savedState && savedState.columnState && savedState.columnState.length > 0) {
                        // Saved settings exist in config.service - apply them (this takes priority over default config)
                        console.log(`✅ Found saved settings in config.service for grid: ${gridId}, applying them...`);
                        console.log(`📋 [GridConfigService] Saved settings contain ${savedState.columnState.length} column states`);

                        // Get existing column definitions and merge saved state into them
                        const existingColumns = gridApi.getColumns();
                        const existingColumnDefs = existingColumns ? existingColumns.map((col) => col.getColDef()) : [];
                        const savedStateMap = new Map(savedState.columnState.map((cs: ColumnState) => [cs.colId, cs]));

                        // Merge saved column state into existing column definitions
                        const mergedColumnDefs: ColDef[] = existingColumnDefs.map((colDef) => {
                            const savedColState = savedStateMap.get(colDef.field as string);
                            if (savedColState) {
                                const isPinned = savedColState.pinned === 'left' || savedColState.pinned === 'right';
                                return {
                                    ...colDef,
                                    pinned: savedColState.pinned || undefined,
                                    lockPinned: isPinned, // Lock pinned columns from saved settings
                                    width: savedColState.width,
                                    hide: savedColState.hide,
                                    sort: savedColState.sort || undefined,
                                    sortIndex: savedColState.sortIndex || undefined,
                                };
                            }
                            return colDef;
                        });
                        // Use centralized configuration method
                        // For master grids, preserve pinned columns from column definitions
                        this.applyGridConfiguration(gridApi, {
                            columnDefs: mergedColumnDefs.length > 0 ? mergedColumnDefs : undefined,
                            filterModel: savedState.gridOptions?.filterModel,
                            paginationPageSize: savedState.gridOptions?.paginationPageSize,
                            applyOrder: true,
                            gridId: gridId,
                            preservePinnedFromColumnDefs: isMasterGrid, // Preserve pinned for master grids
                        }).subscribe({
                            next: (success) => {
                                if (success) {
                                    console.log('✅ Grid settings applied successfully via API');
                                    observer.next(true);
                                } else {
                                    this.setErrorState(gridId, 'Error applying grid settings');
                                    observer.next(false);
                                }
                                observer.complete();
                            },
                            error: (error) => {
                                console.error('❌ Error applying grid settings:', error);
                                this.setErrorState(gridId, 'Error applying grid settings');
                                observer.next(false);
                                observer.complete();
                            },
                        });
                    } else {
                        // No saved settings found - load and apply default column config
                        console.log(`ℹ️ No saved settings to apply for grid: ${gridId}, loading default config for entity: ${entityName}`);

                        this.defaultGridConfigService
                            .getDefaultGridConfig(entityName)
                            .pipe(
                                switchMap((defaultConfig: ColumnConfig[] | null) => {
                                    if (defaultConfig && Array.isArray(defaultConfig) && defaultConfig.length > 0) {
                                        console.log(`📥 [GridConfigService] Default config loaded for first load:`, defaultConfig);
                                        console.log(`✅ [GridConfigService] Found ${defaultConfig.length} columns in default config`);

                                        const existingColumns = gridApi.getColumns();
                                        const existingColumnDefsMap = new Map<string, ColDef>();
                                        if (existingColumns && existingColumns.length > 0) {
                                            existingColumns.forEach((col) => {
                                                const colDef = col.getColDef();
                                                if (colDef.field) {
                                                    existingColumnDefsMap.set(colDef.field as string, colDef);
                                                }
                                            });
                                        }
                                        // Convert default column config to column definitions format (to update column defs)
                                        const columnDefs: ColDef[] = defaultConfig.map((config: ColumnConfig) => {
                                            const isPinned = config.pinned === 'left' || config.pinned === 'right';
                                            const existingColDef = existingColumnDefsMap.get(config.field);
                                            return {
                                                ...(existingColDef || {}),
                                                field: config.field,
                                                headerName: config.headerName || config.field,
                                                sortable: config.sortable ?? true,
                                                width: config.width,
                                                minWidth: config.minWidth,
                                                pinned: config.pinned, // Include pinned in column definitions
                                                lockPinned: isPinned, // Lock pinned columns to prevent auto-unpinning
                                                tooltip: config.tooltip,
                                                hide: config.visible === false,
                                                sort: config.defaultSort,
                                                sortIndex: config.sortIndex,
                                            };
                                        });

                                        // Convert default column config to column state format
                                        const columnState: ColumnState[] = defaultConfig
                                            .map((config: ColumnConfig) => ({
                                                colId: config.field,
                                                hide: config.visible === false,
                                                width: config.width,
                                                pinned: config.pinned || null,
                                                sort: config.defaultSort || null,
                                                sortIndex: config.sortIndex !== undefined ? config.sortIndex : null,
                                            }))
                                            .filter((state: ColumnState) => state.colId);

                                        const pinnedCount = columnState.filter((cs) => cs.pinned === 'left' || cs.pinned === 'right').length;
                                        console.log(`📌 [GridConfigService] Default config has ${pinnedCount} pinned columns`);
                                        console.log(
                                            `📌 [GridConfigService] Pinned columns:`,
                                            columnState.filter((cs) => cs.pinned).map((cs) => ({ colId: cs.colId, pinned: cs.pinned })),
                                        );

                                        if (columnState.length > 0 && columnDefs.length > 0) {
                                            // Sort column state: pinned columns first, then unpinned
                                            // This ensures pinned columns are applied in the correct order
                                            const sortedColumnState = [...columnState].sort((a, b) => {
                                                const aPinned = a.pinned === 'left' || a.pinned === 'right';
                                                const bPinned = b.pinned === 'left' || b.pinned === 'right';
                                                if (aPinned && !bPinned) return -1;
                                                if (!aPinned && bPinned) return 1;
                                                return 0;
                                            });
                                            console.log(
                                                `📌 [GridConfigService] Sorted column state (pinned first):`,
                                                sortedColumnState.map((cs) => ({ colId: cs.colId, pinned: cs.pinned })),
                                            );

                                            // Apply default column config using centralized method
                                            // Use Observable to ensure proper timing
                                            return new Observable<boolean>((observer) => {
                                                // Wait a bit to ensure grid is fully ready
                                                setTimeout(() => {
                                                    if (columnDefs.length > 0) {
                                                        gridApi.setColumnDefs(columnDefs);
                                                        console.log(`📌 [GridConfigService] Column definitions set with ${pinnedCount} pinned columns (filters preserved)`);
                                                    } else {
                                                        console.warn(`⚠️ [GridConfigService] No column definitions to set, preserving existing column definitions`);
                                                    }
                                                    // Then apply column state after a short delay
                                                    setTimeout(() => {
                                                        try {
                                                            // Separate pinned and unpinned columns
                                                            const pinnedState = sortedColumnState.filter((cs) => cs.pinned === 'left' || cs.pinned === 'right');
                                                            const unpinnedState = sortedColumnState.filter((cs) => !cs.pinned || cs.pinned === null);

                                                            console.log(
                                                                `📌 [GridConfigService] Applying ${pinnedState.length} pinned columns and ${unpinnedState.length} unpinned columns`,
                                                            );

                                                            // Use column API to pin columns directly (more reliable than applyColumnState)
                                                            const columns = gridApi.getColumns();
                                                            if (columns && pinnedState.length > 0) {
                                                                pinnedState.forEach((pinState) => {
                                                                    const column = columns.find((col: any) => col.getColId() === pinState.colId);
                                                                    if (column && pinState.pinned) {
                                                                        try {
                                                                            column.setPinned(pinState.pinned);
                                                                            console.log(
                                                                                `📌 [GridConfigService] Pinned column via API: ${pinState.colId} -> ${pinState.pinned}`,
                                                                            );
                                                                        } catch (error) {
                                                                            console.warn(`⚠️ [GridConfigService] Failed to pin ${pinState.colId}:`, error);
                                                                        }
                                                                    }
                                                                });
                                                            }

                                                            // Then apply the full state with order (this will set order, width, visibility, etc.)
                                                            // But don't override pinned state
                                                            const stateWithoutPinned = sortedColumnState.map((cs) => ({
                                                                ...cs,
                                                                pinned: undefined, // Don't set pinned in state, use what we set via API
                                                            }));

                                                            gridApi.applyColumnState({
                                                                state: stateWithoutPinned,
                                                                applyOrder: true,
                                                            });
                                                            // Now re-apply pinned state to ensure it's not overridden
                                                            if (columns && pinnedState.length > 0) {
                                                                pinnedState.forEach((pinState) => {
                                                                    const column = columns.find((col: any) => col.getColId() === pinState.colId);
                                                                    if (column && pinState.pinned) {
                                                                        column.setPinned(pinState.pinned);
                                                                    }
                                                                });
                                                            }

                                                            // Store pinned state for this grid to re-apply on resize
                                                            this.storePinnedStateForGrid(gridId, pinnedState);

                                                            // Add resize listener to ensure pinned columns stay pinned even on smaller screens
                                                            this.ensurePinnedColumnsOnResize(gridApi, gridId, pinnedState);

                                                            // Force grid refresh
                                                            gridApi.refreshCells();

                                                            console.log(`✅ [GridConfigService] Default column config applied on first load for: ${gridId}`);
                                                            console.log(`✅ [GridConfigService] Applied ${pinnedCount} pinned columns`);

                                                            // Double-check by reading back the column state
                                                            setTimeout(() => {
                                                                const actualState = gridApi.getColumnState();
                                                                const actualPinned = actualState.filter((cs) => cs.pinned === 'left' || cs.pinned === 'right');
                                                                console.log(
                                                                    `🔍 [GridConfigService] Actual pinned columns after apply: ${actualPinned.length}`,
                                                                    actualPinned.map((cs) => ({ colId: cs.colId, pinned: cs.pinned })),
                                                                );

                                                                if (actualPinned.length !== pinnedCount) {
                                                                    console.warn(
                                                                        `⚠️ [GridConfigService] Mismatch! Expected ${pinnedCount} pinned columns but found ${actualPinned.length}`,
                                                                    );
                                                                    console.warn(`⚠️ [GridConfigService] Re-applying pinned columns using column API...`);

                                                                    // Use column API to pin columns directly (more reliable than applyColumnState)
                                                                    const columns = gridApi.getColumns();
                                                                    if (columns) {
                                                                        pinnedState.forEach((pinState) => {
                                                                            const column = columns.find((col: any) => col.getColId() === pinState.colId);
                                                                            if (column && pinState.pinned) {
                                                                                try {
                                                                                    column.setPinned(pinState.pinned);
                                                                                    console.log(
                                                                                        `📌 [GridConfigService] Pinned column via API: ${pinState.colId} -> ${pinState.pinned}`,
                                                                                    );
                                                                                } catch (error) {
                                                                                    console.warn(`⚠️ [GridConfigService] Failed to pin ${pinState.colId}:`, error);
                                                                                }
                                                                            }
                                                                        });

                                                                        gridApi.refreshCells();

                                                                        // Verify again
                                                                        setTimeout(() => {
                                                                            const finalState = gridApi.getColumnState();
                                                                            const finalPinned = finalState.filter((cs) => cs.pinned === 'left' || cs.pinned === 'right');
                                                                            console.log(
                                                                                `🔍 [GridConfigService] Final pinned columns after API retry: ${finalPinned.length}`,
                                                                                finalPinned.map((cs) => ({ colId: cs.colId, pinned: cs.pinned })),
                                                                            );
                                                                        }, 200);
                                                                    }
                                                                }
                                                            }, 200);

                                                            observer.next(true);
                                                            observer.complete();
                                                        } catch (error) {
                                                            console.error(`❌ [GridConfigService] Error applying default config:`, error);
                                                            observer.next(false);
                                                            observer.complete();
                                                        }
                                                    }, 100);
                                                }, 200);
                                            });
                                        } else {
                                            console.log(`ℹ️ [GridConfigService] No valid column state/defs generated from default config`);
                                            return of(false);
                                        }
                                    } else {
                                        console.log(`ℹ️ [GridConfigService] No default column config found for entity: ${entityName}`);
                                        return of(false);
                                    }
                                }),
                                catchError((error) => {
                                    console.warn(`⚠️ [GridConfigService] Error loading default column config for ${entityName}:`, error);
                                    return of(false);
                                }),
                            )
                            .subscribe({
                                next: (success) => {
                                    observer.next(success);
                                    observer.complete();
                                },
                                error: (error) => {
                                    console.error(`❌ [GridConfigService] Error applying default config:`, error);
                                    observer.next(false);
                                    observer.complete();
                                },
                            });
                    }
                },
                error: (error) => {
                    console.error('❌ Error loading settings for application:', error);
                    this.setErrorState(gridId, 'Error loading settings for application');
                    observer.next(false);
                    observer.complete();
                },
            });
        });
    }

    resetGridSettings(gridId: string, gridApi?: GridApi): Observable<{ success: boolean; message: string }> {
        if (!gridId) {
            console.warn('Cannot reset: Grid ID not provided');
            return new Observable((observer) => {
                observer.error({ success: false, message: 'Grid ID not provided' });
            });
        }

        // Set loading state
        this.setLoadingState(gridId, true);
        this.setErrorState(gridId, null);

        return new Observable((observer) => {
            // Extract entity name from grid ID (for master grids: new-master-grid-{entityName})
            const isMasterGrid = gridId.startsWith('new-master-grid-');
            const entityName = isMasterGrid ? gridId.replace('new-master-grid-', '') : gridId;

            // First try to delete saved settings, then always load and apply default config
            this.configService
                .deleteConfig({ name: gridId })
                .pipe(
                    switchMap((result: any) => {
                        if (result.success) {
                            console.log(`✅ Grid settings deleted successfully for: ${gridId}`);
                        } else {
                            console.log(`ℹ️ No saved settings to delete for: ${gridId}, will apply default config`);
                        }
                        // Always load default column config and apply it to the grid (even if no settings were deleted)
                        if (gridApi) {
                            console.log(`🔄 [GridConfigService] Loading default column config for entity: ${entityName}`);
                            return this.defaultGridConfigService.getDefaultGridConfig(entityName).pipe(
                                switchMap((defaultConfig: ColumnConfig[] | null) => {
                                    console.log(`📥 [GridConfigService] Default config loaded:`, defaultConfig);

                                    if (defaultConfig && Array.isArray(defaultConfig) && defaultConfig.length > 0) {
                                        console.log(`✅ [GridConfigService] Found ${defaultConfig.length} columns in default config`);

                                        // Convert default column config to column definitions format
                                        const columnDefs: ColDef[] = defaultConfig.map((config: ColumnConfig) => {
                                            const isPinned = config.pinned === 'left' || config.pinned === 'right';
                                            return {
                                                field: config.field,
                                                headerName: config.headerName || config.field,
                                                sortable: config.sortable ?? true,
                                                width: config.width,
                                                minWidth: config.minWidth,
                                                pinned: config.pinned, // Include pinned in column definitions
                                                lockPinned: isPinned, // Lock pinned columns to prevent auto-unpinning
                                                tooltip: config.tooltip,
                                                hide: config.visible === false,
                                                sort: config.defaultSort,
                                                sortIndex: config.sortIndex,
                                            };
                                        });

                                        // Convert default column config to column state format
                                        const columnState: ColumnState[] = defaultConfig
                                            .map((config: ColumnConfig) => ({
                                                colId: config.field,
                                                hide: config.visible === false,
                                                width: config.width,
                                                pinned: config.pinned || null,
                                                sort: config.defaultSort || null,
                                                sortIndex: config.sortIndex !== undefined ? config.sortIndex : null,
                                            }))
                                            .filter((state: ColumnState) => state.colId); // Filter out invalid states
                                        // Sort column state: pinned columns first, then unpinned
                                        const sortedColumnState = [...columnState].sort((a, b) => {
                                            const aPinned = a.pinned === 'left' || a.pinned === 'right';
                                            const bPinned = b.pinned === 'left' || b.pinned === 'right';
                                            if (aPinned && !bPinned) return -1;
                                            if (!aPinned && bPinned) return 1;
                                            return 0;
                                        });

                                        const pinnedCount = columnState.filter((cs) => cs.pinned === 'left' || cs.pinned === 'right').length;
                                        console.log(`📌 [GridConfigService] Default config has ${pinnedCount} pinned columns`);
                                        console.log(
                                            `📌 [GridConfigService] Pinned columns:`,
                                            columnState.filter((cs) => cs.pinned).map((cs) => ({ colId: cs.colId, pinned: cs.pinned })),
                                        );

                                        if (columnState.length > 0 && columnDefs.length > 0) {
                                            // Apply column state using the same reliable method as first load
                                            return new Observable<{ success: boolean; message: string }>((observer) => {
                                                setTimeout(() => {
                                                    if (gridApi) {
                                                        try {
                                                            // First set column definitions with pinned values
                                                            gridApi.setColumnDefs(columnDefs);
                                                            console.log(`📌 [GridConfigService] Column definitions set with ${pinnedCount} pinned columns`);

                                                            setTimeout(() => {
                                                                // Separate pinned and unpinned columns
                                                                const pinnedState = sortedColumnState.filter((cs) => cs.pinned === 'left' || cs.pinned === 'right');

                                                                console.log(`📌 [GridConfigService] Applying ${pinnedState.length} pinned columns via column API`);

                                                                // Use column API to pin columns directly (more reliable than applyColumnState)
                                                                const columns = gridApi.getColumns();
                                                                if (columns && pinnedState.length > 0) {
                                                                    pinnedState.forEach((pinState) => {
                                                                        const column = columns.find((col: any) => col.getColId() === pinState.colId);
                                                                        if (column && pinState.pinned) {
                                                                            try {
                                                                                column.setPinned(pinState.pinned);
                                                                                console.log(
                                                                                    `📌 [GridConfigService] Pinned column via API: ${pinState.colId} -> ${pinState.pinned}`,
                                                                                );
                                                                            } catch (error) {
                                                                                console.warn(`⚠️ [GridConfigService] Failed to pin ${pinState.colId}:`, error);
                                                                            }
                                                                        }
                                                                    });
                                                                }

                                                                // Then apply the full state with order (this will set order, width, visibility, etc.)
                                                                // But don't override pinned state
                                                                const stateWithoutPinned = sortedColumnState.map((cs) => ({
                                                                    ...cs,
                                                                    pinned: undefined, // Don't set pinned in state, use what we set via API
                                                                }));

                                                                gridApi.applyColumnState({
                                                                    state: stateWithoutPinned,
                                                                    applyOrder: true,
                                                                });

                                                                // Now re-apply pinned state to ensure it's not overridden
                                                                if (columns && pinnedState.length > 0) {
                                                                    pinnedState.forEach((pinState) => {
                                                                        const column = columns.find((col: any) => col.getColId() === pinState.colId);
                                                                        if (column && pinState.pinned) {
                                                                            column.setPinned(pinState.pinned);
                                                                        }
                                                                    });
                                                                }
                                                                // Store pinned state for this grid to re-apply on resize
                                                                this.storePinnedStateForGrid(gridId, pinnedState);

                                                                // Add resize listener to ensure pinned columns stay pinned
                                                                this.ensurePinnedColumnsOnResize(gridApi, gridId, pinnedState);

                                                                // Force grid to refresh
                                                                gridApi.refreshCells();

                                                                console.log(`✅ [GridConfigService] Default column config applied successfully to grid for: ${gridId}`);
                                                                console.log(`✅ [GridConfigService] Applied ${pinnedCount} pinned columns`);

                                                                // Verify pinned columns were applied
                                                                setTimeout(() => {
                                                                    const actualState = gridApi.getColumnState();
                                                                    const actualPinned = actualState.filter((cs) => cs.pinned === 'left' || cs.pinned === 'right');
                                                                    console.log(
                                                                        `🔍 [GridConfigService] Actual pinned columns after reset: ${actualPinned.length}`,
                                                                        actualPinned.map((cs) => ({ colId: cs.colId, pinned: cs.pinned })),
                                                                    );

                                                                    if (actualPinned.length !== pinnedCount) {
                                                                        console.warn(
                                                                            `⚠️ [GridConfigService] Mismatch! Expected ${pinnedCount} pinned columns but found ${actualPinned.length}`,
                                                                        );
                                                                        console.warn(`⚠️ [GridConfigService] Re-applying pinned columns using column API...`);

                                                                        // Retry pinning via column API
                                                                        if (columns) {
                                                                            pinnedState.forEach((pinState) => {
                                                                                const column = columns.find((col: any) => col.getColId() === pinState.colId);
                                                                                if (column && pinState.pinned) {
                                                                                    try {
                                                                                        column.setPinned(pinState.pinned);
                                                                                        console.log(
                                                                                            `📌 [GridConfigService] Re-pinned column via API: ${pinState.colId} -> ${pinState.pinned}`,
                                                                                        );
                                                                                    } catch (error) {
                                                                                        console.warn(`⚠️ [GridConfigService] Failed to re-pin ${pinState.colId}:`, error);
                                                                                    }
                                                                                }
                                                                            });

                                                                            gridApi.refreshCells();

                                                                            // Verify again
                                                                            setTimeout(() => {
                                                                                const finalState = gridApi.getColumnState();
                                                                                const finalPinned = finalState.filter((cs) => cs.pinned === 'left' || cs.pinned === 'right');
                                                                                console.log(
                                                                                    `🔍 [GridConfigService] Final pinned columns after reset retry: ${finalPinned.length}`,
                                                                                    finalPinned.map((cs) => ({ colId: cs.colId, pinned: cs.pinned })),
                                                                                );
                                                                            }, 200);
                                                                        }
                                                                    }
                                                                }, 200);

                                                                observer.next(result);
                                                                observer.complete();
                                                            }, 100);
                                                        } catch (error) {
                                                            console.error(`❌ [GridConfigService] Error applying default column config:`, error);
                                                            observer.next(result);
                                                            observer.complete();
                                                        }
                                                    } else {
                                                        console.warn(`⚠️ [GridConfigService] Grid API not available`);
                                                        observer.next(result);
                                                        observer.complete();
                                                    }
                                                }, 200); // Increased timeout to ensure grid is ready
                                            });
                                        } else {
                                            console.log(`ℹ️ [GridConfigService] No valid column state/defs generated from default config for: ${entityName}`);
                                            return of(result);
                                        }
                                    } else {
                                        console.log(`ℹ️ [GridConfigService] No default column config found for entity: ${entityName}`);
                                        console.log(`ℹ️ [GridConfigService] Default config value:`, defaultConfig);
                                        return of(result);
                                    }
                                }),
                                catchError((error) => {
                                    console.error(`❌ [GridConfigService] Error loading default column config for ${entityName}:`, error);
                                    return of(result); // Continue even if default config load fails
                                }),
                            );
                        } else {
                            console.warn(`⚠️ [GridConfigService] No grid API provided, cannot apply default config`);
                            // No grid API, just return success
                            return of({ success: true, message: 'Reset completed (no grid API to apply config)' });
                        }
                    }),
                )
                .subscribe({
                    next: (result) => {
                        this.setLoadingState(gridId, false);
                        if (result.success) {
                            console.log(`✅ Grid settings reset successfully via API for: ${gridId}`);
                            this.alertService.alert(AlertType.Success, result.message, 'Success');
                            observer.next(result);
                        } else {
                            this.setErrorState(gridId, result.message);
                            this.alertService.alert(AlertType.Warning, result.message, 'Warning');
                            observer.next(result);
                        }
                        observer.complete();
                    },
                    error: (error) => {
                        this.setLoadingState(gridId, false);
                        this.setErrorState(gridId, error.message || 'Error resetting grid settings');
                        console.error('❌ Error resetting grid settings via API:', error);
                        this.alertService.alert(AlertType.Error, 'Error resetting grid settings', 'Error');
                        observer.error(error);
                    },
                });
        });
    }

    getLoadingState(gridId: string): Observable<boolean> {
        if (!this.loadingStates.has(gridId)) {
            this.loadingStates.set(gridId, new BehaviorSubject<boolean>(false));
        }
        return this.loadingStates.get(gridId)!.asObservable();
    }

    getErrorState(gridId: string): Observable<string | null> {
        if (!this.errorStates.has(gridId)) {
            this.errorStates.set(gridId, new BehaviorSubject<string | null>(null));
        }
        return this.errorStates.get(gridId)!.asObservable();
    }
    private setLoadingState(gridId: string, loading: boolean): void {
        if (!this.loadingStates.has(gridId)) {
            this.loadingStates.set(gridId, new BehaviorSubject<boolean>(false));
        }
        this.loadingStates.get(gridId)!.next(loading);
    }

    private setErrorState(gridId: string, error: string | null): void {
        if (!this.errorStates.has(gridId)) {
            this.errorStates.set(gridId, new BehaviorSubject<string | null>(null));
        }
        this.errorStates.get(gridId)!.next(error);
    }

    applyColumnState(
        gridApi: GridApi,
        columnState: ColumnState[],
        options?: {
            applyOrder?: boolean;
            defaultState?: Partial<ColumnState>;
            gridId?: string; // Optional grid ID for logging
            preservePinnedFromColumnDefs?: boolean; // Preserve pinned from column definitions
        },
    ): Observable<boolean> {
        if (!gridApi) {
            console.error('❌ [GridConfigService] Cannot apply column state: Grid API not provided');
            return of(false);
        }

        if (!columnState || !Array.isArray(columnState)) {
            console.warn('⚠️ [GridConfigService] Column state is not a valid array');
            return of(false);
        }

        const gridId = options?.gridId || 'unknown';
        console.log(`🔄 [GridConfigService] Applying column state to grid: ${gridId}`, {
            columnCount: columnState.length,
            applyOrder: options?.applyOrder ?? false,
            preservePinned: options?.preservePinnedFromColumnDefs ?? false,
        });
        console.log('columnState', columnState);
        console.log('options', options);
        console.log('gridId', gridId);
        console.log('gridApi', gridApi);
        console.log('gridApi.getColumns()', gridApi.getColumns());
        try {
            let stateToApply = columnState;
            // If we need to preserve pinned from column definitions
            if (options?.preservePinnedFromColumnDefs) {
                const columns = gridApi.getColumns();

                // Create a map of pinned state from column definitions
                const pinnedFromColumnDefs = new Map<string, 'left' | 'right' | null | undefined>();

                if (columns && columns.length > 0) {
                    columns.forEach((col) => {
                        const colDef = col.getColDef();
                        const colId = col.getColId();

                        // Read pinned state directly from column definition
                        // Filter out false (which means unpinned)
                        if (colDef.pinned !== null && colDef.pinned !== undefined && colDef.pinned !== false) {
                            pinnedFromColumnDefs.set(colId, colDef.pinned as 'left' | 'right');
                        } else if (colDef.pinned === false) {
                            // Explicitly handle false as null (unpinned)
                            pinnedFromColumnDefs.set(colId, null);
                        }
                    });

                    if (pinnedFromColumnDefs.size > 0) {
                        const pinnedCount = Array.from(pinnedFromColumnDefs.values()).filter((p) => p !== null && p !== undefined).length;
                        console.log(`📌 [GridConfigService] Found ${pinnedCount} pinned columns from column definitions`);

                        // Merge saved column state with pinned state from column definitions
                        stateToApply = columnState.map((colState: any) => {
                            const pinnedFromDef = pinnedFromColumnDefs.get(colState.colId);
                            if (pinnedFromDef !== undefined) {
                                return {
                                    ...colState,
                                    pinned: pinnedFromDef,
                                };
                            }
                            return colState;
                        });
                        // Add any columns that exist in column definitions but not in saved state
                        columns.forEach((col) => {
                            const colId = col.getColId();
                            const colDef = col.getColDef();
                            if (!stateToApply.find((cs: any) => cs.colId === colId)) {
                                if (colDef.pinned !== null && colDef.pinned !== undefined) {
                                    stateToApply.push({
                                        colId: colId,
                                        pinned: colDef.pinned,
                                        hide: colDef.hide ?? false,
                                        width: colDef.width,
                                    });
                                }
                            }
                        });

                        const finalPinnedCount = stateToApply.filter((cs: any) => cs.pinned === 'left' || cs.pinned === 'right').length;
                        console.log(`✅ [GridConfigService] Merged state has ${finalPinnedCount} pinned columns`);
                    }
                }
            }
            const params: ApplyColumnStateParams = {
                state: stateToApply,
                applyOrder: options?.applyOrder ?? false,
            };

            if (options?.defaultState) {
                params.defaultState = options.defaultState;
            }

            gridApi.applyColumnState(params);
            console.log(`✅ [GridConfigService] Column state applied successfully to grid: ${gridId}`);
            return of(true);
        } catch (error) {
            console.error(`❌ [GridConfigService] Error applying column state to grid: ${gridId}`, error);
            return of(false);
        }
    }

    setColumnDefs(gridApi: GridApi, columnDefs: ColDef[], gridId?: string): Observable<boolean> {
        if (!gridApi) {
            console.error('❌ [GridConfigService] Cannot set column definitions: Grid API not provided');
            return of(false);
        }

        if (!columnDefs || !Array.isArray(columnDefs)) {
            console.warn('⚠️ [GridConfigService] Column definitions is not a valid array');
            return of(false);
        }

        const id = gridId || 'unknown';
        console.log(`🔄 [GridConfigService] Setting column definitions for grid: ${id}`, {
            columnCount: columnDefs.length,
        });

        try {
            // Ensure lockPinned is set for pinned columns to prevent auto-unpinning
            const columnDefsWithLockPinned = columnDefs.map((colDef) => {
                const isPinned = colDef.pinned === 'left' || colDef.pinned === 'right';
                return {
                    ...colDef,
                    lockPinned: isPinned ? colDef.lockPinned !== false : colDef.lockPinned,
                };
            });
            gridApi.setColumnDefs(columnDefsWithLockPinned);
            console.log(`✅ [GridConfigService] Column definitions set successfully for grid: ${id}`);
            return of(true);
        } catch (error) {
            console.error(`❌ [GridConfigService] Error setting column definitions for grid: ${id}`, error);
            return of(false);
        }
    }

    resetGridState(
        gridApi: GridApi,
        options?: {
            originalColumnDefs?: ColDef[];
            defaultPaginationSize?: number;
            clearFilters?: boolean;
            clearSorting?: boolean;
            clearPinning?: boolean;
            autoSizeColumns?: boolean;
            gridId?: string; // Optional grid ID for logging
        },
    ): Observable<boolean> {
        if (!gridApi) {
            console.error('❌ [GridConfigService] Cannot reset grid state: Grid API not provided');
            return of(false);
        }

        const gridId = options?.gridId || 'unknown';
        console.log(`🔄 [GridConfigService] Resetting grid state for grid: ${gridId}`, options);

        try {
            // Reset column definitions if provided
            if (options?.originalColumnDefs && options.originalColumnDefs.length > 0) {
                const resetColumnDefs = options.originalColumnDefs.map((col) => ({
                    ...col,
                    width: col.width || 100,
                    minWidth: col.minWidth || 5,
                    sort: undefined,
                    sortIndex: undefined,
                }));
                gridApi.setColumnDefs(resetColumnDefs);
            }

            // Reset column state (visibility, pinning)
            const defaultState: Partial<ColumnState> = {};
            if (options?.clearPinning !== false) {
                defaultState.pinned = null;
            }
            if (options?.clearSorting !== false) {
                defaultState.sort = null;
                defaultState.sortIndex = null;
            }

            gridApi.applyColumnState({
                state: [],
                defaultState: {
                    hide: false,
                    ...defaultState,
                },
            });

            // Reset pagination if provided
            if (options?.defaultPaginationSize) {
                gridApi.paginationSetPageSize(options.defaultPaginationSize);
            }

            // Clear filters if requested
            if (options?.clearFilters !== false) {
                gridApi.setFilterModel(null);
                gridApi.onFilterChanged();
            }

            // Refresh grid
            gridApi.refreshCells();

            // Auto-size columns if requested
            if (options?.autoSizeColumns) {
                setTimeout(() => {
                    gridApi.autoSizeAllColumns();
                }, 100);
            }

            console.log(`✅ [GridConfigService] Grid state reset successfully for grid: ${gridId}`);
            return of(true);
        } catch (error) {
            console.error(`❌ [GridConfigService] Error resetting grid state for grid: ${gridId}`, error);
            return of(false);
        }
    }

    applyGridConfiguration(
        gridApi: GridApi,
        config: {
            columnDefs?: ColDef[];
            columnState?: ColumnState[];
            filterModel?: any;
            paginationPageSize?: number;
            applyOrder?: boolean;
            gridId?: string;
            preservePinnedFromColumnDefs?: boolean; // New option to preserve pinned from column definitions
        },
    ): Observable<boolean> {
        if (!gridApi) {
            console.error('❌ [GridConfigService] Cannot apply grid configuration: Grid API not provided');
            return of(false);
        }

        const gridId = config.gridId || 'unknown';
        console.log(`🔄 [GridConfigService] Applying grid configuration for grid: ${gridId}`, config);

        try {
            // Apply column definitions first if provided
            if (config.columnDefs && config.columnDefs.length > 0) {
                // Ensure lockPinned is set for pinned columns
                const columnDefsWithLockPinned = config.columnDefs.map((colDef) => {
                    const isPinned = colDef.pinned === 'left' || colDef.pinned === 'right';
                    return {
                        ...colDef,
                        lockPinned: isPinned ? colDef.lockPinned !== false : colDef.lockPinned,
                    };
                });
                gridApi.setColumnDefs(columnDefsWithLockPinned);
            }

            // Apply column state if provided
            if (config.columnState && config.columnState.length > 0) {
                let columnStateToApply = config.columnState;

                // If we need to preserve pinned from column definitions (for master grids)
                if (config.preservePinnedFromColumnDefs !== false) {
                    const isMasterGrid = gridId.startsWith('new-master-grid-');

                    // Get columns and read pinned state directly from column definitions
                    const columns = gridApi.getColumns();

                    // Create a map of ALL column state from column definitions (not just pinned)
                    const columnStateFromDefs = new Map<string, ColumnState>();
                    // Check if columns are initialized and read state from column definitions
                    if (columns && columns.length > 0) {
                        columns.forEach((col) => {
                            const colDef = col.getColDef();
                            const colId = col.getColId();

                            // Read ALL state from column definition, not just pinned
                            const colState: ColumnState = {
                                colId: colId,
                                hide: colDef.hide ?? false,
                                width: colDef.width,
                                pinned: colDef.pinned !== undefined && colDef.pinned !== false ? colDef.pinned : null,
                                sort: colDef.sort || null,
                                sortIndex: colDef.sortIndex || null,
                            };

                            columnStateFromDefs.set(colId, colState);
                        });

                        if (columnStateFromDefs.size > 0) {
                            const pinnedCount = Array.from(columnStateFromDefs.values()).filter((cs) => cs.pinned === 'left' || cs.pinned === 'right').length;
                            console.log(`📌 [GridConfigService] Found ${pinnedCount} pinned columns from column definitions for grid: ${gridId}`);
                            console.log(`📌 [GridConfigService] Total columns from definitions: ${columnStateFromDefs.size}`);

                            // Create a map of saved state for quick lookup
                            const savedStateMap = new Map<string, ColumnState>();
                            config.columnState.forEach((cs: ColumnState) => {
                                savedStateMap.set(cs.colId, cs);
                            });

                            // Merge: Start with column definitions as base, then override with saved state (except pinned)
                            // Priority: pinned from column definitions > everything else from saved state
                            columnStateToApply = Array.from(columnStateFromDefs.values()).map((defState: ColumnState) => {
                                const savedState = savedStateMap.get(defState.colId);

                                if (savedState) {
                                    // Column exists in saved state - merge but preserve pinned from definition
                                    return {
                                        ...savedState,
                                        pinned: defState.pinned !== null && defState.pinned !== undefined ? defState.pinned : savedState.pinned,
                                    };
                                } else {
                                    // Column only exists in definitions - use definition state
                                    return defState;
                                }
                            });
                            // Also add any columns that exist in saved state but not in definitions
                            config.columnState.forEach((savedState: ColumnState) => {
                                if (!columnStateFromDefs.has(savedState.colId)) {
                                    columnStateToApply.push(savedState);
                                }
                            });

                            const finalPinnedCount = columnStateToApply.filter((cs: any) => cs.pinned === 'left' || cs.pinned === 'right').length;
                            console.log(`✅ [GridConfigService] Merged state has ${finalPinnedCount} pinned columns for grid: ${gridId}`);
                            console.log(`✅ [GridConfigService] Total columns in merged state: ${columnStateToApply.length}`);
                        }
                    }
                }

                gridApi.applyColumnState({
                    state: columnStateToApply,
                    applyOrder: config.applyOrder ?? false,
                });

                // Extract pinned state and store it for resize handling
                const pinnedState = columnStateToApply.filter((cs) => cs.pinned === 'left' || cs.pinned === 'right');
                if (pinnedState.length > 0) {
                    // Store pinned state for this grid to re-apply on resize
                    this.storePinnedStateForGrid(gridId, pinnedState);

                    // Add resize listener to ensure pinned columns stay pinned even on smaller screens
                    setTimeout(() => {
                        this.ensurePinnedColumnsOnResize(gridApi, gridId, pinnedState);
                    }, 100);
                }
            }
            // Apply pagination size if provided
            if (config.paginationPageSize) {
                gridApi.paginationSetPageSize(config.paginationPageSize);
            }

            // Apply filters if provided
            if (config.filterModel) {
                gridApi.setFilterModel(config.filterModel);
                gridApi.onFilterChanged();
            }

            console.log(`✅ [GridConfigService] Grid configuration applied successfully for grid: ${gridId}`);
            return of(true);
        } catch (error) {
            console.error(`❌ [GridConfigService] Error applying grid configuration for grid: ${gridId}`, error);
            return of(false);
        }
    }

    private storePinnedStateForGrid(gridId: string, pinnedState: ColumnState[]): void {
        this.pinnedStateMap.set(gridId, pinnedState);
    }

    private ensurePinnedColumnsOnResize(gridApi: GridApi, gridId: string, pinnedState: ColumnState[]): void {
        // Remove existing listener if any
        if (this.resizeListeners.has(gridId)) {
            const removeListener = this.resizeListeners.get(gridId);
            if (removeListener) {
                gridApi.removeEventListener('gridSizeChanged', removeListener);
            }
        }

        // Create new listener to re-apply pinned columns on resize
        const resizeListener = () => {
            // Re-apply pinned columns after resize to ensure they stay pinned
            const columns = gridApi.getColumns();
            if (columns && pinnedState.length > 0) {
                // Check current pinned state
                const currentState = gridApi.getColumnState();
                const currentPinned = currentState.filter((cs) => cs.pinned === 'left' || cs.pinned === 'right');

                // If not all pinned columns are pinned, re-apply them
                if (currentPinned.length < pinnedState.length) {
                    console.log(`🔄 [GridConfigService] Re-applying ${pinnedState.length} pinned columns after resize for: ${gridId}`);
                    pinnedState.forEach((pinState) => {
                        const column = columns.find((col: any) => col.getColId() === pinState.colId);
                        if (column && pinState.pinned) {
                            try {
                                column.setPinned(pinState.pinned);
                            } catch (error) {
                                console.warn(`⚠️ [GridConfigService] Failed to re-pin ${pinState.colId} after resize:`, error);
                            }
                        }
                    });
                    gridApi.refreshCells();
                }
            }
        };
        // Store listener for cleanup
        this.resizeListeners.set(gridId, resizeListener);

        // Add event listener
        gridApi.addEventListener('gridSizeChanged', resizeListener);

        // Also listen for column moved/pinned events to ensure pinned state is maintained
        const columnPinnedListener = () => {
            // Small delay to let AG Grid finish its operation
            setTimeout(() => {
                const currentState = gridApi.getColumnState();
                const currentPinned = currentState.filter((cs) => cs.pinned === 'left' || cs.pinned === 'right');
                const expectedPinned = pinnedState.length;

                if (currentPinned.length < expectedPinned) {
                    console.log(`🔄 [GridConfigService] Re-applying pinned columns after column event for: ${gridId}`);
                    const columns = gridApi.getColumns();
                    if (columns) {
                        pinnedState.forEach((pinState) => {
                            const column = columns.find((col: any) => col.getColId() === pinState.colId);
                            if (column && pinState.pinned) {
                                const currentPinnedValue = column.getPinned();
                                if (currentPinnedValue !== pinState.pinned) {
                                    column.setPinned(pinState.pinned);
                                }
                            }
                        });
                    }
                }
            }, 100);
        };

        gridApi.addEventListener('columnPinnedChanged', columnPinnedListener);
        gridApi.addEventListener('columnMoved', columnPinnedListener);

        // Store column event listener for cleanup
        this.columnEventListeners.set(gridId, columnPinnedListener);
    }

    public cleanupGridListeners(gridId: string, gridApi?: GridApi): void {
        if (gridApi) {
            // Remove resize listener
            if (this.resizeListeners.has(gridId)) {
                const removeListener = this.resizeListeners.get(gridId);
                if (removeListener) {
                    gridApi.removeEventListener('gridSizeChanged', removeListener);
                }
                this.resizeListeners.delete(gridId);
            }

            // Remove column event listeners
            if (this.columnEventListeners.has(gridId)) {
                const columnListener = this.columnEventListeners.get(gridId);
                if (columnListener) {
                    gridApi.removeEventListener('columnPinnedChanged', columnListener);
                    gridApi.removeEventListener('columnMoved', columnListener);
                }
                this.columnEventListeners.delete(gridId);
            }
        }
        this.pinnedStateMap.delete(gridId);
    }
}
